import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TransactionPage.css'; // Reuse styles
import './PortfolioMasterPage.css';
import { API_BASE_URL } from '../services/api';

const initialState = {
  portfolio_id: '',
  portfolio_name: '',
  portfolio_type: '',
  entity_business_unit: '',
  fund_manager_user_id: '',
  base_currency: '',
  benchmark: '',
  start_date: '',
  end_date: '',
  status: 'Active',
  risk_profile: '',
  investment_horizon: '',
  target_yield_return: '',
  compliance_rules_id: '',
  notes_description: '',
  parent_portfolio_id: '',
  valuation_method: '',
  accounting_treatment: '',
  rebalancing_frequency: '',
  external_reference_code: '',
  tags_categories: '',
};

function PortfolioMasterPage() {
  const [form, setForm] = useState(initialState);
  const [portfolios, setPortfolios] = useState([]);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchPortfolios();
  }, []);

  const fetchPortfolios = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/portfolio-master`);
      setPortfolios(res.data);
    } catch (err) {
      setError('Failed to load portfolios');
    }
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      if (editing) {
        await axios.put(`${API_BASE_URL}/portfolio-master/${form.portfolio_id}`, form);
        setSuccess('Portfolio updated');
      } else {
        await axios.post(`${API_BASE_URL}/portfolio-master`, form);
        setSuccess('Portfolio created');
      }
      setForm(initialState);
      setEditing(false);
      fetchPortfolios();
    } catch (err) {
      setError('Error saving portfolio');
    }
  };

  const handleEdit = p => {
    setForm(p);
    setEditing(true);
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this portfolio?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/portfolio-master/${id}`);
      fetchPortfolios();
    } catch (err) {
      setError('Error deleting portfolio');
    }
  };

  return (
    <div className="page-container ui-page">
      <h2 style={{ color: '#222', textAlign: 'center', marginBottom: '1.5rem' }}>Portfolio Master</h2>
      <form className="form-section compact-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label style={{ color: '#222', fontWeight: 500 }}>Portfolio ID</label>
            <input name="portfolio_id" value={form.portfolio_id} onChange={handleChange} required disabled={editing} />
          </div>
          <div className="form-group">
            <label style={{ color: '#222', fontWeight: 500 }}>Portfolio Name</label>
            <input name="portfolio_name" value={form.portfolio_name} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label style={{ color: '#222', fontWeight: 500 }}>Portfolio Type</label>
            <input name="portfolio_type" value={form.portfolio_type} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label style={{ color: '#222', fontWeight: 500 }}>Entity/Business Unit</label>
            <input name="entity_business_unit" value={form.entity_business_unit} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label style={{ color: '#222', fontWeight: 500 }}>Fund Manager/User ID</label>
            <input name="fund_manager_user_id" value={form.fund_manager_user_id} onChange={handleChange} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label style={{ color: '#222', fontWeight: 500 }}>Base Currency</label>
            <input name="base_currency" value={form.base_currency} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label style={{ color: '#222', fontWeight: 500 }}>Benchmark</label>
            <input name="benchmark" value={form.benchmark} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label style={{ color: '#222', fontWeight: 500 }}>Start Date</label>
            <input type="date" name="start_date" value={form.start_date} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label style={{ color: '#222', fontWeight: 500 }}>End Date</label>
            <input type="date" name="end_date" value={form.end_date} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label style={{ color: '#222', fontWeight: 500 }}>Status</label>
            <select name="status" value={form.status} onChange={handleChange}>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label style={{ color: '#222', fontWeight: 500 }}>Risk Profile</label>
            <select name="risk_profile" value={form.risk_profile} onChange={handleChange}>
              <option value="">Select</option>
              <option value="Conservative">Conservative</option>
              <option value="Moderate">Moderate</option>
              <option value="Aggressive">Aggressive</option>
            </select>
          </div>
          <div className="form-group">
            <label style={{ color: '#222', fontWeight: 500 }}>Investment Horizon</label>
            <select name="investment_horizon" value={form.investment_horizon} onChange={handleChange}>
              <option value="">Select</option>
              <option value="Short-term">Short-term</option>
              <option value="Medium-term">Medium-term</option>
              <option value="Long-term">Long-term</option>
            </select>
          </div>
          <div className="form-group">
            <label style={{ color: '#222', fontWeight: 500 }}>Target Yield / Return</label>
            <input type="number" step="0.0001" name="target_yield_return" value={form.target_yield_return} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label style={{ color: '#222', fontWeight: 500 }}>Compliance Rules ID</label>
            <input name="compliance_rules_id" value={form.compliance_rules_id} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label style={{ color: '#222', fontWeight: 500 }}>Notes / Description</label>
            <input name="notes_description" value={form.notes_description} onChange={handleChange} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label style={{ color: '#222', fontWeight: 500 }}>Parent Portfolio ID</label>
            <input name="parent_portfolio_id" value={form.parent_portfolio_id} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label style={{ color: '#222', fontWeight: 500 }}>Valuation Method</label>
            <input name="valuation_method" value={form.valuation_method} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label style={{ color: '#222', fontWeight: 500 }}>Accounting Treatment</label>
            <input name="accounting_treatment" value={form.accounting_treatment} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label style={{ color: '#222', fontWeight: 500 }}>Rebalancing Frequency</label>
            <input name="rebalancing_frequency" value={form.rebalancing_frequency} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label style={{ color: '#222', fontWeight: 500 }}>External Reference Code</label>
            <input name="external_reference_code" value={form.external_reference_code} onChange={handleChange} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label style={{ color: '#222', fontWeight: 500 }}>Tags / Categories</label>
            <input name="tags_categories" value={form.tags_categories} onChange={handleChange} />
          </div>
        </div>
        <div className="form-row">
          <button type="submit" className="save-verify-button">{editing ? 'Update' : 'Create'} Portfolio</button>
          {editing && <button type="button" className="action-button" onClick={() => { setForm(initialState); setEditing(false); }}>Cancel</button>}
        </div>
        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}
      </form>
      <div className="transactions-section">
        <h3>Existing Portfolios</h3>
        <table className="transactions-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Type</th>
              <th>Entity</th>
              <th>Manager</th>
              <th>Currency</th>
              <th>Status</th>
              <th>Risk</th>
              <th>Horizon</th>
              <th>Yield</th>
              <th>Parent</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {portfolios.map(p => (
              <tr key={p.portfolio_id}>
                <td>{p.portfolio_id}</td>
                <td>{p.portfolio_name}</td>
                <td>{p.portfolio_type}</td>
                <td>{p.entity_business_unit}</td>
                <td>{p.fund_manager_user_id}</td>
                <td>{p.base_currency}</td>
                <td>{p.status}</td>
                <td>{p.risk_profile}</td>
                <td>{p.investment_horizon}</td>
                <td>{p.target_yield_return}</td>
                <td>{p.parent_portfolio_id}</td>
                <td>
                  <button className="action-button edit-button" onClick={() => handleEdit(p)}>Edit</button>
                  <button className="action-button delete-button" onClick={() => handleDelete(p.portfolio_id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PortfolioMasterPage;
